// File: src/pages/profile/sections/ArtistProfile.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  writeBatch,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";

// Discography Assets
import discographyIcon from "../../../assets/discography.png";
import discographyDark from "../../../assets/discography_darkmode.png";
import discographyActive from "../../../assets/discography_active.png";

// Albums Assets
import albumsIcon from "../../../assets/albums.png";
import albumsDark from "../../../assets/albums_darkmode.png";
import albumsActive from "../../../assets/albums_active.png";

// Press Assets
import pressIcon from "../../../assets/press.png";
import pressDark from "../../../assets/press_darkmode.png";
import pressActive from "../../../assets/press_active.png";

// Fans Assets
import fanIcon from "../../../assets/fan.png";
import fanDark from "../../../assets/fan_darkmode.png";
import fanActive from "../../../assets/fan_active.png";

// Profile context
import { useProfile } from "../../../context/ProfileContext";
import useUrlTab from '../../../hooks/useUrlTab';
import toast from 'react-hot-toast';

import SongTile from "../../../components/music/SongTile";
import SortableSongTile from "../../../components/music/SortableSongTile";
import SongEditModal from "../../../components/music/SongEditModal";

import AlbumTile from '../../../components/albums/AlbumTile';
import CreateAlbumModal from '../../../components/albums/CreateAlbumModal';

import PressPost from "../../../components/press/PressPost";
import AddToPlaylistModal from "../../../components/music/AddToPlaylistModal";
import AUDTippingModal from '../../../components/music/AUDTipModal';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import FanLeaderboard from "../../../components/fans/FanLeaderboard";

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const ArtistProfile = ({ 
  artistProfile, 
  isPublicView = false, 
  setQueueAndPlay, 
  posts = [], 
  fetchPosts, 
  onEditPost, 
  isPostsLoading, 
  artistProfiles = {} 
}) => {
  const [activeTab, handleTabChange] = useUrlTab('discography');
  const previousTab = usePrevious(activeTab);
  const [songs, setSongs] = useState([]);
  const [initialSongs, setInitialSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const { isDarkMode, user } = useProfile();

  // State for albums and create modal
  const [albums, setAlbums] = useState([]);
  const [isAlbumsLoading, setIsAlbumsLoading] = useState(false);
  const [isCreateAlbumModalOpen, setIsCreateAlbumModalOpen] = useState(false);

  const [playlistModalState, setPlaylistModalState] = useState({ isOpen: false, songToAdd: null });
  const [tipModalState, setTipModalState] = useState({ isOpen: false, songToTip: null });

  const [deletingPost, setDeletingPost] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const deletePost = useMemo(() => {
    const functions = getFunctions();
    return httpsCallable(functions, 'deletePost');
  }, []);

  const navigate = useNavigate();
  const db = getFirestore();

  const openEditModal = (song) => setEditingSong(song);
  const closeEditModal = () => setEditingSong(null);

  const handleOpenPlaylistModal = (song) => {
    if (!song) return;
    setPlaylistModalState({ isOpen: true, songToAdd: song });
  };
  const handleClosePlaylistModal = () => {
    setPlaylistModalState({ isOpen: false, songToAdd: null });
  };

  const handleOpenTipModal = (song) => {
    if (!song) return;
    setTipModalState({ isOpen: true, songToTip: song });
  };
  const handleCloseTipModal = () => {
    setTipModalState({ isOpen: false, songToTip: null });
  };

  const handleDeleteClick = (post) => {
    setDeletingPost(post);
  };

  const confirmDelete = async () => {
    if (!deletingPost) return;
    setIsDeleting(true);

    try {
      await deletePost({ postId: deletingPost.id });
      toast.success("Post deleted successfully.");
      fetchPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post. Please try again.");
    } finally {
      setIsDeleting(false);
      setDeletingPost(null);
    }
  };

  const handlePlaySong = (clickedSongIndex) => {
    const queue = songs.map(s => ({ id: s.id }));
    const source = `Discography: ${artistProfile.artistName}`;
    setQueueAndPlay(queue, clickedSongIndex, source);
  };

  const fetchSongs = async () => {
    if (!artistProfile?.id) return;
    setIsLoading(true);
    try {
      const songsRef = collection(db, "songs");
      const q = query(
        songsRef,
        where("artistId", "==", artistProfile.id),
        where("isDeleted", "!=", true),
        orderBy("order", "asc")
      );
      const querySnapshot = await getDocs(q);
      const fetchedSongs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setSongs(fetchedSongs);
      setInitialSongs(fetchedSongs);
    } catch (error) {
      console.error("Firestore Query Failed:", error); 
      toast.error("Failed to fetch songs. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSongs((items) => {
        const oldIndex = items.findIndex(song => song.id === active.id);
        const newIndex = items.findIndex(song => song.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = useCallback(async () => {
    const hasOrderChanged = JSON.stringify(initialSongs) !== JSON.stringify(songs);
    if (!hasOrderChanged) return;

    const batch = writeBatch(db);
    songs.forEach((song, index) => {
      const songRef = doc(db, 'songs', song.id);
      batch.update(songRef, { order: index });
    });

    try {
      await batch.commit();
      setInitialSongs(songs);
    } catch (error) {
      console.error("Error saving order: ", error);
      toast.error("Could not save order.");
    }
  }, [songs, initialSongs, db]);

  const saveCallback = useRef();
  useEffect(() => {
    saveCallback.current = handleSaveOrder;
  }, [handleSaveOrder]);

  const fetchAlbums = async () => {
    if (!artistProfile?.id) return;
    setIsAlbumsLoading(true);
    try {
      const albumsRef = collection(db, "albums");
      const q = query(albumsRef, where("artistId", "==", artistProfile.id), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const fetchedAlbums = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAlbums(fetchedAlbums);
    } catch (error) {
      toast.error("Failed to fetch albums.");
    } finally {
      setIsAlbumsLoading(false);
    }
  };

  const getTabIcon = (tabName) => {
    if (activeTab === tabName) {
      if (tabName === "discography") return discographyActive;
      if (tabName === "albums") return albumsActive;
      if (tabName === "press") return pressActive;
      if (tabName === "fans") return fanActive;
    }
    if (isDarkMode) {
      if (tabName === "discography") return discographyDark;
      if (tabName === "albums") return albumsDark;
      if (tabName === "press") return pressDark;
      if (tabName === "fans") return fanDark;
    }
    if (tabName === "discography") return discographyIcon;
    if (tabName === "albums") return albumsIcon;
    if (tabName === "press") return pressIcon;
    return fanIcon;
  };

  const [viewerFanScore, setViewerFanScore] = useState(0);

  useEffect(() => {
    if (!artistProfile?.id) return;
    if (activeTab === "discography") {
      fetchSongs();
    } else if (activeTab === "albums") {
      fetchAlbums();
    }
  }, [activeTab, artistProfile]); 

  useEffect(() => {
    if (previousTab === 'discography' && activeTab !== 'discography') {
      handleSaveOrder();
    }
  }, [activeTab, previousTab, handleSaveOrder]);

  useEffect(() => {
    return () => {
      if (activeTab === 'discography') {
        saveCallback.current();
      }
    };
  }, [activeTab]);

  useEffect(() => {
    if (!user?.uid || !artistProfile?.id) return;
    const fetchViewerScore = async () => {
      try {
        const fanRef = doc(db, `users/${user.uid}/artistsTipped/${artistProfile.id}`);
        const fanSnap = await getDoc(fanRef);
        if (fanSnap.exists()) {
          const points = fanSnap.data().totalPointsTipped || fanSnap.data().tipAmountAUD || 0;
          setViewerFanScore(Number(points));
        } else {
          setViewerFanScore(0);
        }
      } catch (err) {
        console.error("Error fetching viewer FanScore:", err);
        setViewerFanScore(0);
      }
    };
    fetchViewerScore();
  }, [user?.uid, artistProfile?.id, db]);

  return (
    <div className="relative pb-24 transition-colors">
      
      {/* Tab Navigation Array */}
      <div className="flex justify-center mt-8 space-x-6 sm:space-x-8">
        {["discography", "albums", "press", "fans"].map((tab) => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className="flex flex-col items-center group w-16 sm:w-20"
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                activeTab === tab ? "bg-transparent scale-110" : "bg-transparent"
              }`}
            >
              <img
                src={getTabIcon(tab)}
                alt={tab}
                className="w-10 h-10 object-contain"
              />
            </div>
            
            <p className={`text-[10px] mt-1 font-bold uppercase tracking-widest transition-colors ${
              activeTab === tab ? "text-[#ff00cc]" : "text-gray-500 dark:text-brand-white"
            }`}>
              {tab}
            </p>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="mt-8 px-4 w-full max-w-screen-md mx-auto">
        
        {/* DISCOGRAPHY TAB */}
        {activeTab === "discography" && (
          <div>
            {isLoading && <p className="text-center text-gray-500">Loading...</p>}
            {!isLoading && (
              isPublicView ? (
                <div className="grid grid-cols-3 gap-4">
                  {songs.map((song, index) => (
                    <SongTile key={song.id} song={song} onClick={() => handlePlaySong(index)} isInteractive />
                  ))}
                </div>
              ) : (
                <div>
                  <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={songs.map(s => s.id)} strategy={rectSortingStrategy}>
                      <div className="grid grid-cols-3 gap-4">
                        {songs.map((song, index) => (
                          <SortableSongTile 
                            key={song.id} 
                            song={song} 
                            onEdit={() => openEditModal(song)} 
                            onClick={() => handlePlaySong(index)} 
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )
            )}
          </div>
        )}

        {/* ALBUMS TAB */}
        {activeTab === "albums" && (
          <div>
            {isAlbumsLoading && <p className="text-center text-gray-500">Loading albums...</p>}
            {!isAlbumsLoading && (
              <div className="grid grid-cols-3 gap-4">
                {albums.map(album => (
                  <AlbumTile key={album.id} album={album} onClick={() => navigate(`/album/${album.id}`)} />
                ))}
                
                {!isPublicView && (
                  <button
                    onClick={() => setIsCreateAlbumModalOpen(true)}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* PRESS TAB */}
        {activeTab === "press" && (
          <div>
            {isPostsLoading && <p className="text-center text-gray-500 py-4">Loading posts...</p>}
            {!isPostsLoading && posts.length === 0 && (
              <p className="text-center text-gray-500 py-4">No press posts found.</p>
            )}
            {!isPostsLoading && posts.length > 0 && (
              <div className="flex flex-col items-center space-y-4 max-w-2xl mx-auto">
                {posts.map((post) => (
                  <PressPost 
                    key={post.id} 
                    post={post} 
                    artistProfile={artistProfiles[post.artistId] || artistProfile} 
                    onAddToPlaylistClick={handleOpenPlaylistModal} 
                    onTipClick={handleOpenTipModal} 
                    sourceContext="artistProfile" 
                    isOwner={!isPublicView} 
                    onEdit={onEditPost} 
                    onDelete={handleDeleteClick} 
                    userFanScore={viewerFanScore} 
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* FANS TAB */}
        {activeTab === "fans" && artistProfile?.id && (
          <div className="w-full">
            <FanLeaderboard 
              artistId={artistProfile.id} 
              isDashboardView={false} 
              allowSelfTipping={false} 
              hideOwner={true} 
              showOwnerBadge={false} 
            />
          </div>
        )}

      </div>

      {/* Helper Modals */}
      {editingSong && (
        <SongEditModal
          song={editingSong}
          onClose={closeEditModal}
          onSave={(updatedFields) => {
            setSongs((prev) =>
              prev.map((s) => s.id === editingSong.id ? { ...s, ...updatedFields } : s)
            );
          }}
        />
      )} 
      
      <AddToPlaylistModal 
        isOpen={playlistModalState.isOpen}
        onClose={handleClosePlaylistModal}
        songToAdd={playlistModalState.songToAdd}
      />
      
      <AUDTippingModal 
        isOpen={tipModalState.isOpen}
        onClose={handleCloseTipModal}
        songToTip={tipModalState.songToTip}
      />
      
      <ConfirmDeleteModal 
        isOpen={!!deletingPost}
        onClose={() => setDeletingPost(null)}
        onConfirm={confirmDelete}
        itemName="post"
        isDeleting={isDeleting}
      />

      <CreateAlbumModal 
        isOpen={isCreateAlbumModalOpen} 
        onClose={() => {
          setIsCreateAlbumModalOpen(false);
          fetchAlbums();
        }}
      />
    </div>
  );
};

export default ArtistProfile;